type MediaKind = "audio" | "video";

export interface MetadataCueDescriptor {
  id: string;
  start: number;
  end: number;
  data?: Record<string, unknown>;
}

export interface MetadataCueListeners {
  onEnter?: (descriptor: MetadataCueDescriptor) => void;
  onExit?: (descriptor: MetadataCueDescriptor) => void;
}

interface RegistrationOptions {
  onAttachChange?: (attached: boolean) => void;
}

interface CueRegistration {
  sourceId: string;
  descriptors: MetadataCueDescriptor[];
  listeners: MetadataCueListeners;
  options?: RegistrationOptions;
  attachedCues: TextTrackCue[];
  track: TextTrack | null;
  attached: boolean;
}

type CueConstructor = new (
  startTime: number,
  endTime: number,
  text: string
) => TextTrackCue;

const registrations = new Map<string, CueRegistration>();

let activeElement: HTMLMediaElement | null = null;
let activeKind: MediaKind | null = null;
let activeTrack: TextTrack | null = null;
const logCue = (...args: unknown[]) => {
  if (process.env.NODE_ENV !== "production") {
    console.log("[media-cue-registry]", ...args);
  }
};

const cueConstructor: CueConstructor | null =
  typeof window !== "undefined"
    ? ((window as unknown as Record<string, unknown>).VTTCue as CueConstructor | undefined) ||
      ((window as unknown as Record<string, unknown>).TextTrackCue as CueConstructor | undefined) ||
      null
    : null;

function ensureMetadataTrack(element: HTMLMediaElement): TextTrack | null {
  try {
    const existing = Array.from(element.textTracks || []).find(
      (track) => track.kind === "metadata" && track.label === "nf-practice"
    );

    if (existing) {
      existing.mode = "hidden";
      // logCue("reuse metadata track", {
      //   readyState: element.readyState,
      //   tag: element.tagName,
      // });
      return existing;
    }

    if (typeof element.addTextTrack === "function") {
      const created = element.addTextTrack("metadata", "nf-practice", "en");
      created.mode = "hidden";
      logCue("created metadata track", {
        readyState: element.readyState,
        tag: element.tagName,
      });
      return created;
    }
  } catch (error) {
    console.warn("[media-cue-registry] Failed to ensure metadata track:", error);
  }

  return null;
}

function updateAttachmentState(registration: CueRegistration, attached: boolean) {
  if (registration.attached === attached) {
    return;
  }
  registration.attached = attached;
  // logCue("attachment change", {
  //   sourceId: registration.sourceId,
  //   attached,
  //   cueCount: registration.descriptors.length,
  // });
  registration.options?.onAttachChange?.(attached);
}

function detachRegistration(registration: CueRegistration) {
  if (registration.attachedCues.length && registration.track) {
    registration.attachedCues.forEach((cue) => {
      try {
        registration.track?.removeCue(cue);
      } catch {
        // ignore removal errors
      }
    });
  }

  registration.attachedCues = [];
  registration.track = null;
  updateAttachmentState(registration, false);
}

function detachAllRegistrations() {
  registrations.forEach(detachRegistration);
}

function attachRegistration(registration: CueRegistration) {
  const track = activeTrack;

  if (!track || !cueConstructor) {
    // logCue("attach skipped (missing track/cueConstructor)", {
    //   hasTrack: Boolean(track),
    //   hasCueConstructor: Boolean(cueConstructor),
    //   sourceId: registration.sourceId,
    // });
    updateAttachmentState(registration, false);
    return;
  }

  detachRegistration(registration);

  const cues: TextTrackCue[] = [];

  registration.descriptors.forEach((descriptor) => {
    const start = Number(descriptor.start);
    let end = Number(descriptor.end);

    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      return;
    }

    if (end <= start) {
      end = start + 0.05;
    }

    try {
      const cue = new cueConstructor(start, end, descriptor.id);
      cue.onenter = () => registration.listeners.onEnter?.(descriptor);
      cue.onexit = () => registration.listeners.onExit?.(descriptor);

      track.addCue(cue);
      // logCue("attached cue", {
      //   sourceId: registration.sourceId,
      //   id: descriptor.id,
      //   start,
      //   end,
      // });
      cues.push(cue);
    } catch (error) {
      console.warn("[media-cue-registry] Failed to add cue:", {
        error,
        descriptor,
      });
    }
  });

  registration.attachedCues = cues;
  registration.track = track;
  updateAttachmentState(registration, cues.length > 0);
}

function attachAllRegistrations() {
  // logCue("attachAllRegistrations", {
  //   count: registrations.size,
  //   hasActiveTrack: Boolean(activeTrack),
  //   activeKind,
  // });
  registrations.forEach(attachRegistration);
}

export const mediaCueRegistry = {
  setActiveMediaElement(element: HTMLMediaElement | null, kind: MediaKind) {
    if (element === activeElement) {
      return;
    }

    detachAllRegistrations();

    activeElement = element;
    activeKind = element ? kind : null;
    // logCue("setActiveMediaElement", {
    //   hasElement: Boolean(element),
    //   kind,
    // });

    activeTrack = element ? ensureMetadataTrack(element) : null;

    if (activeTrack && cueConstructor) {
      attachAllRegistrations();
    } else {
      registrations.forEach((registration) =>
        updateAttachmentState(registration, false)
      );
    }
  },

  clearActiveMediaElement(element?: HTMLMediaElement) {
    if (element && element !== activeElement) {
      return;
    }

    detachAllRegistrations();
    activeElement = null;
    activeTrack = null;
    activeKind = null;

    registrations.forEach((registration) =>
      updateAttachmentState(registration, false)
    );
  },

  registerCueSet(
    sourceId: string,
    descriptors: MetadataCueDescriptor[],
    listeners: MetadataCueListeners,
    options?: RegistrationOptions
  ): () => void {
    if (!descriptors || descriptors.length === 0) {
      const noopRegistration: CueRegistration = {
        sourceId,
        descriptors: [],
        listeners,
        options,
        attachedCues: [],
        track: null,
        attached: false,
      };

      registrations.set(sourceId, noopRegistration);
      options?.onAttachChange?.(false);

      return () => {
        registrations.delete(sourceId);
      };
    }

    const registration: CueRegistration = {
      sourceId,
      descriptors,
      listeners,
      options,
      attachedCues: [],
      track: null,
      attached: false,
    };

    registrations.set(sourceId, registration);

    if (activeTrack && cueConstructor) {
      attachRegistration(registration);
    } else {
      options?.onAttachChange?.(false);
    }

    return () => {
      detachRegistration(registration);
      registrations.delete(sourceId);
    };
  },

  getActiveMediaElement(): HTMLMediaElement | null {
    return activeElement;
  },

  getActiveMediaKind(): MediaKind | null {
    return activeKind;
  },

  hasCueSupport(): boolean {
    return Boolean(cueConstructor);
  },
};
